// Shared bits for the Voice calling surface.
import React, { useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import moment from 'moment';
import { Loader2, MoreHorizontal, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { voiceKeys } from '@/client/api/voice/voice.query';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorCode } from '@/client/utils/errors';
import { BoxStatus, VoiceBox, VoiceChannel } from '@/client/types/voice';
import { cloudErrorText } from '../Cloud/cloudUi';

export { LoadingNote } from '../Cloud/cloudUi';

export const VOICE_BASE = '/admin/communications/calling';

/* ---------- permissions ---------- */

export const useVoicePerms = () => {
  const view = useHasPermission('voice.view');
  const manage = useHasPermission('voice.manage');
  const recordings = useHasPermission('voice.recordings');
  return {
    loading: view.isLoading,
    canView: view.isSuperAdmin || view.hasPermission,
    canManage: manage.isSuperAdmin || manage.hasPermission,
    canRecordings: recordings.isSuperAdmin || recordings.hasPermission,
  };
};
export type VoicePerms = ReturnType<typeof useVoicePerms>;

/* ---------- formatting ---------- */

export const fmtWhen = (iso?: string | null) => (iso ? moment(iso).format('D MMM YYYY, HH:mm:ss') : '—');
export const fmtShort = (iso?: string | null) => (iso ? moment(iso).format('D MMM, HH:mm') : '—');
export const fmtAgoIso = (iso?: string | null) => (iso ? moment(iso).fromNow() : 'never');

export const fmtTalk = (sec?: number | null) => {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  return `${m}m ${Math.round(sec % 60)}s`;
};

export const fmtMs = (ms?: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`);

export const fmtBytes = (n?: number | null) =>
  n == null ? '—' : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

export const titleCase = (s: string) =>
  s.replace(/[_.]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/* ---------- state pills ---------- */

const CALL_STATE: Record<string, string> = {
  completed: 'bg-green-50 text-green-700 ring-green-200',
  bridged: 'bg-blue-50 text-blue-700 ring-blue-200',
  failed: 'bg-red-50 text-red-700 ring-red-200',
  expired: 'bg-amber-50 text-amber-700 ring-amber-200',
  cancelled: 'bg-slate-100 text-slate-600 ring-slate-200',
};
const LIVE_STYLE = 'bg-sky-50 text-sky-700 ring-sky-200';

export const CallStatePill: React.FC<{ state: string }> = ({ state }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
      CALL_STATE[state] ?? LIVE_STYLE
    }`}
  >
    {titleCase(state)}
  </span>
);

const BOX_STATUS: Record<BoxStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 ring-amber-200',
  active: 'bg-green-50 text-green-700 ring-green-200',
  draining: 'bg-sky-50 text-sky-700 ring-sky-200',
  disabled: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export const BoxStatusPill: React.FC<{ status: BoxStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
      BOX_STATUS[status] ?? BOX_STATUS.disabled
    }`}
  >
    {titleCase(status)}
  </span>
);

export const OnlineDot: React.FC<{ online: boolean; lastSeen?: string | null }> = ({
  online,
  lastSeen,
}) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
    <span className={`h-2 w-2 rounded-full ${online ? 'bg-green-500' : 'bg-red-500'}`} />
    {online ? 'Online' : 'Offline'}
    <span className="text-gray-400">· {fmtAgoIso(lastSeen)}</span>
  </span>
);

/** "1 of 1 in / 1 of 1 out": free modems out of the modems the box has for each direction. */
export const freeModems = (b: Pick<VoiceBox, 'channels' | 'inbound_free' | 'outbound_free'>) => {
  const ch = b.channels ?? [];
  const inTotal = ch.filter((c) => c.role === 'inbound').length || 1;
  const outTotal = ch.filter((c) => c.role === 'outbound').length || 1;
  return `${b.inbound_free ?? 0} of ${inTotal} in / ${b.outbound_free ?? 0} of ${outTotal} out`;
};

/** SIM7600 RSSI is 0-31 (99 unknown); 2 dBm per step from -113. */
export const signalText = (c: VoiceChannel) => {
  const r = c.signal_rssi;
  if (r == null || r === 99) return null;
  return `${r}/31 (${-113 + 2 * r} dBm)`;
};

/* ---------- loading, errors, access ---------- */

export const FetchingChip: React.FC = () =>
  useIsFetching({ queryKey: voiceKeys.all }) > 0 ? (
    <span
      role="status"
      className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
    >
      <Loader2 className="h-3 w-3 animate-spin" />
      Updating
    </span>
  ) : null;

const ERROR_COPY: Record<string, string> = {
  required_slot: 'The recording notice is required: make another version active instead.',
  prompt_active: 'This announcement is active. Make another version active first, then delete it.',
  invalid_audio: 'That file is not audio the boxes can play.',
  invalid_slot: 'That announcement slot does not exist.',
  too_large: 'That file is over 5 MB.',
  invalid_setting: 'One of the settings is out of range.',
  edge_unavailable: 'The box could not be reached.',
  no_recording: 'This call has no recording.',
  capacity_exhausted: 'No box has a free pair of modems.',
  missing_reason: 'A reason is required.',
};

export const voiceErrorText = (error: unknown): string => {
  const code = getApiErrorCode(error);
  return code && ERROR_COPY[code] ? ERROR_COPY[code] : cloudErrorText(error);
};

export const ErrorNotice: React.FC<{ error: unknown; onRetry?: () => void }> = ({
  error,
  onRetry,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
    <span>{voiceErrorText(error)}</span>
    {onRetry && (
      <Button size="sm" variant="outline" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    )}
  </div>
);

export const AccessDenied: React.FC = () => (
  <div className="rounded-xl border border-gray-100 bg-white p-10 text-center shadow-sm">
    <p className="text-sm font-medium text-gray-800">You don&apos;t have access to Voice calling</p>
    <p className="mt-1 text-sm text-gray-500">Ask an administrator for the voice.view permission.</p>
  </div>
);

/* ---------- audio ---------- */

/** Plays straight from the admin URL; nothing is fetched until the user presses play. */
export const AudioPlayer: React.FC<{ src: string; className?: string }> = ({ src, className }) => (
  // eslint-disable-next-line jsx-a11y/media-has-caption
  <audio controls preload="none" src={src} className={`h-9 w-full max-w-xs ${className ?? ''}`} />
);

/* ---------- the dotted menu ---------- */

export type KebabItem = {
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
  hidden?: boolean;
};

/** One dotted menu per row; each entry has an icon. Dialogs belong to the caller. */
export const Kebab: React.FC<{ name: string; items: KebabItem[] }> = ({ name, items }) => {
  const shown = items.filter((i) => !i.hidden);
  if (!shown.length) return null;
  return (
    // Not modal: a modal menu handing over to a dialog can leave the page unclickable.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          aria-label={`Actions for ${name}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="truncate">{name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {shown.map((i) => (
          <DropdownMenuItem
            key={i.label}
            className={`cursor-pointer gap-2 ${i.danger ? 'text-red-600 focus:text-red-700' : ''}`}
            onSelect={i.onSelect}
          >
            {i.icon} {i.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Small hook for the "which confirmation is open" state every page needs. */
export const useDialog = <T,>() => useState<T | null>(null);
